import { MESSAGE_CAPTCHA_FAILURE, MESSAGE_CAPTCHA_UNAVAILABLE } from '@/utils/validation-utils';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useReCaptchaReady } from '@/app/provider/ReCaptchaProvider';
import { useSnackbar } from 'notistack';

/**
 * The longest to wait for reCAPTCHA to become able to issue a token, in milliseconds.
 */
const CAPTCHA_READY_TIMEOUT = 15_000;

/**
 * How often the wait re-reads the captcha's state, in milliseconds.
 */
const CAPTCHA_POLL_INTERVAL = 100;

/**
 * The longest to wait for reCAPTCHA to answer with a token, in milliseconds.
 */
const CAPTCHA_TOKEN_TIMEOUT = 15_000;

/**
 * Issues a token for an action. Absent until the reCAPTCHA script has loaded.
 */
type ReCaptchaExecutor = (action: string) => Promise<string>;

/**
 * The captcha state a call in flight reads, rather than the values of the render it started in.
 */
interface CaptchaState {
  ready: boolean;
  scriptFailed: boolean;
  executeRecaptcha: ReCaptchaExecutor | undefined;
  reloadScript: () => boolean;
  markScriptUnusable: () => void;
}

interface UseReCaptchaResult {
  /**
   * Whether the captcha is being waited on or a token is being obtained,
   * so a caller can show its action as running.
   */
  pending: boolean;

  /**
   * Obtains a token for the action, reporting a failure to the user itself.
   */
  obtainToken: () => Promise<string | null>;
}

/**
 * Obtains reCAPTCHA tokens for one action, waiting out a captcha that is still loading
 * and reloading a script that failed to load.
 *
 * @param action The name reported to reCAPTCHA for the action being protected.
 */
export function useReCaptcha(action: string): UseReCaptchaResult {
  const { enqueueSnackbar } = useSnackbar();
  const { ready, scriptFailed, reloadScript, markScriptUnusable } = useReCaptchaReady();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [pending, setPending] = useState<boolean>(false);

  /**
   * The captcha as it stands right now.
   * A call already under way holds the values of the render it started in,
   * so the wait below reads them from here and sees the captcha become usable while it is waiting.
   */
  const captchaRef: RefObject<CaptchaState> = useRef<CaptchaState>({
    ready,
    scriptFailed,
    executeRecaptcha,
    reloadScript,
    markScriptUnusable,
  });
  useEffect((): void => {
    captchaRef.current = { ready, scriptFailed, executeRecaptcha, reloadScript, markScriptUnusable };
  }, [ready, scriptFailed, executeRecaptcha, reloadScript, markScriptUnusable]);

  /**
   * Whether the component this hook belongs to is gone.
   * A submit outlives its component, and its continuation would otherwise keep polling
   * and go on to complete the action against a store that outlived the form.
   */
  const cancelled: RefObject<boolean> = useRef<boolean>(false);
  useEffect((): (() => void) => {
    cancelled.current = false;
    return (): void => {
      cancelled.current = true;
    };
  }, []);

  const report = useCallback(
    (message: string): void => {
      enqueueSnackbar(message, {
        variant: 'error',
        preventDuplicate: true,
      });
    },
    [enqueueSnackbar],
  );

  const obtainToken = useCallback(async (): Promise<string | null> => {
    /**
     * The executor, but only once the captcha can actually issue a token.
     *
     * @return The executor, or nothing while the captcha is unusable.
     */
    const usableExecutor = (): ReCaptchaExecutor | undefined =>
      captchaRef.current.ready ? captchaRef.current.executeRecaptcha : undefined;

    /**
     * Asks reCAPTCHA for a token, giving up once the request has taken too long.
     *
     * @param executor The executor to ask.
     * @return The token, or nothing if the request did not answer in time.
     */
    const obtainWithinTimeout = async (executor: ReCaptchaExecutor): Promise<string | undefined> => {
      let expiry: ReturnType<typeof setTimeout> | undefined = undefined;
      const timeout: Promise<undefined> = new Promise<undefined>((resolve: (token: undefined) => void): void => {
        expiry = setTimeout((): void => resolve(undefined), CAPTCHA_TOKEN_TIMEOUT);
      });
      try {
        return await Promise.race([executor(action), timeout]);
      } finally {
        clearTimeout(expiry);
      }
    };

    /**
     * Waits until reCAPTCHA can issue a token, giving up early when the load it is waiting on fails
     * or when the component that started the wait is gone.
     *
     * @return The executor, or nothing if the captcha did not become usable.
     */
    const waitForExecutor = async (): Promise<ReCaptchaExecutor | undefined> => {
      const deadline: number = Date.now() + CAPTCHA_READY_TIMEOUT;
      let reloaded: boolean = false;
      let executor: ReCaptchaExecutor | undefined = usableExecutor();
      while (!executor && Date.now() < deadline) {
        await new Promise<void>((resolve: () => void): void => {
          setTimeout(resolve, CAPTCHA_POLL_INTERVAL);
        });
        if (cancelled.current) {
          return undefined;
        }
        // Read before the failure flag, which lags a render behind
        // and can still say the script is broken when this very tick made the captcha usable.
        executor = usableExecutor();
        if (!executor && captchaRef.current.scriptFailed) {
          if (reloaded) {
            // The reload started below failed in its turn, so the remaining deadline buys nothing.
            console.warn('The reloaded ReCaptcha script failed as well.', { action });
            return undefined;
          }
          // The script was still loading when this wait began, so the reload attempted before it was a no-op.
          // Now that the load has actually failed, retry it here rather than making the user click again.
          console.info('The ReCaptcha script failed while waiting for it; reloading it.', { action });
          reloaded = captchaRef.current.reloadScript();
        }
      }
      return executor;
    };

    setPending(true);
    try {
      let executor: ReCaptchaExecutor | undefined = usableExecutor();
      if (!executor) {
        // A script that has already failed never retries on its own, so the wait below would run out again.
        // One that is merely still loading is left alone here and reloaded by the wait if it then fails.
        captchaRef.current.reloadScript();
        executor = await waitForExecutor();
      }
      if (cancelled.current) {
        // Nobody is waiting for this any more, so it is abandoned rather than reported.
        return null;
      }
      if (!executor) {
        console.warn('ReCaptcha did not become usable!', {
          action,
          ready: captchaRef.current.ready,
          executorReady: Boolean(captchaRef.current.executeRecaptcha),
          scriptFailed: captchaRef.current.scriptFailed,
        });
        captchaRef.current.markScriptUnusable();
        report(MESSAGE_CAPTCHA_UNAVAILABLE);
        return null;
      }
      console.log('Starting ReCaptcha verification.', { action });
      const token: string | undefined = await obtainWithinTimeout(executor);
      if (cancelled.current) {
        return null;
      }
      if (!token) {
        console.warn('ReCaptcha did not answer with a token in time!', { action });
        report(MESSAGE_CAPTCHA_FAILURE);
        return null;
      }
      console.log('ReCaptcha verification successful.', { action });
      return token;
    } catch (error) {
      console.error('ReCaptcha verification failed!', {
        action,
        error,
      });
      report(MESSAGE_CAPTCHA_FAILURE);
      return null;
    } finally {
      setPending(false);
    }
  }, [action, report]);

  return { pending, obtainToken };
}
